import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, Plus, LogIn, Loader2, ChevronRight, MessageSquare, MailOpen, Check, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { W } from "@/lib/edition";

const Invites = () => {
  const [list, setList] = useState([]);
  const nav = useNavigate();
  const load = () => api.get("/invites").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const answer = async (inv, action) => {
    try {
      const { data } = await api.post(`/invites/${inv.invite_id}/${action}`);
      if (action === "accept") { toast.success(`You joined ${inv.group_name}`); nav(`/groups/${data.group_id}`); } else load();
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };
  if (!list.length) return null;
  return (
    <section data-testid="group-invites" className="space-y-2">
      <p className="eyebrow flex items-center gap-2"><MailOpen className="w-4 h-4" />Invites for you</p>
      {list.map((inv) => (
        <div key={inv.invite_id} data-testid={`invite-${inv.invite_id}`} className="card flex flex-wrap items-center gap-3 p-4 !border-gold-400/50 animate-rise">
          <p className="flex-1 min-w-0 text-sm text-slate-800"><b className="text-white">{inv.from_name}</b> invited you to <b className="text-gold-300">{inv.group_name}</b></p>
          <button data-testid={`invite-accept-${inv.invite_id}`} onClick={() => answer(inv, "accept")} className="inline-flex items-center gap-1.5 rounded-full bg-gold-400 px-4 py-2 text-xs font-bold text-black hover:bg-gold-300"><Check className="w-3.5 h-3.5" />Accept</button>
          <button data-testid={`invite-decline-${inv.invite_id}`} onClick={() => answer(inv, "decline")} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700"><X className="w-3.5 h-3.5" />Decline</button>
        </div>
      ))}
    </section>
  );
};

const CreateCard = () => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/groups", { name: name.trim() }); toast.success(`Group made. Code: ${data.code}`); nav(`/groups/${data.group_id}`); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={create} data-testid="group-create-form" className="card p-5 sm:p-6 border-b-4 !border-b-gold-400">
      <p className="eyebrow"><W s="Start a group" c="Start a team" /></p>
      <h2 className="mt-1 font-display text-3xl text-white"><W s="Make a group" c="Make a team" /></h2>
      <p className="mt-1 text-sm text-slate-600">Get a 6-letter code to share with your class or friends.</p>
      <div className="mt-4 flex gap-2">
        <input data-testid="group-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="e.g. 10C Chemistry"
          className="flex-1 min-w-0 h-12 rounded-2xl border border-slate-200 bg-surface-deep/60 px-4 text-white placeholder:text-slate-400 outline-none focus:border-gold-400" />
        <button data-testid="group-create-button" disabled={busy || !name.trim()} className="inline-flex items-center gap-2 h-12 rounded-2xl bg-gold-400 px-5 font-bold text-black hover:bg-gold-300 disabled:opacity-40 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Create
        </button>
      </div>
    </form>
  );
};

const JoinCard = () => {
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 6));
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const join = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/groups/join", { code }); toast.success(`You joined ${data.name}`); nav(`/groups/${data.group_id}`); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={join} data-testid="group-join-form" className="card p-5 sm:p-6 border-b-4 !border-b-brand">
      <p className="eyebrow !text-rose-600">Got a code?</p>
      <h2 className="mt-1 font-display text-3xl text-white"><W s="Join a group" c="Join a team" /></h2>
      <p className="mt-1 text-sm text-slate-600">Type the 6 letters someone shared with you.</p>
      <div className="mt-4 flex gap-2">
        <input data-testid="group-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 6))} placeholder="ABCDEF" inputMode="text" autoCapitalize="characters"
          className="flex-1 min-w-0 h-12 rounded-2xl border border-slate-200 bg-surface-deep/60 px-4 font-mono text-xl tracking-[0.4em] text-white placeholder:text-slate-300 outline-none focus:border-brand" />
        <button data-testid="group-join-button" disabled={busy || code.length !== 6} className="inline-flex items-center gap-2 h-12 rounded-2xl bg-brand px-5 font-bold text-white hover:bg-brand-dark disabled:opacity-40 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}Join
        </button>
      </div>
    </form>
  );
};

export default function Groups() {
  const [groups, setGroups] = useState(null);
  useEffect(() => { api.get("/groups").then((r) => setGroups(Array.isArray(r.data) ? r.data : [])).catch(() => setGroups([])); }, []);
  return (
    <main data-testid="groups-page" className="mx-auto max-w-5xl px-4 sm:px-6 py-8 sm:py-12 space-y-8">
      <div>
        <p className="eyebrow"><W s="Groups" c="Teams" /></p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-none"><W s="Study together" c="Work together" /></h1>
        <p className="mt-3 max-w-xl text-slate-600"><W s="Chat, share notes and posts, revise the same packs with a leaderboard, and see your class timetable side by side." c="Chat, share notes and posts, learn from the same packs, and see your team's schedules side by side." /></p>
      </div>
      <Invites />
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 [&>*]:min-w-0"><CreateCard /><JoinCard /></div>
      <section>
        <p className="eyebrow"><W s="Your groups" c="Your teams" /></p>
        {groups === null && <div className="mt-3 h-24 rounded-3xl bg-surface animate-pulse" />}
        {groups?.length === 0 && <p data-testid="groups-empty" className="mt-3 rounded-3xl border-2 border-dashed border-slate-200 p-8 text-center text-slate-500">No groups yet. Make one or join with a code.</p>}
        <ul className="mt-3 grid gap-3 sm:grid-cols-2 stagger">
          {(groups ?? []).map((g) => (
            <li key={g.group_id}>
              <Link to={`/groups/${g.group_id}`} data-testid={`group-card-${g.group_id}`} className="card card-hover flex items-center gap-4 p-4 sm:p-5">
                <span className="grid place-items-center w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-navy-600 to-navy-200 font-display text-2xl text-white">{g.name.slice(0, 1).toUpperCase()}</span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-white truncate">{g.name}</span>
                  <span className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500"><Users className="w-3.5 h-3.5" />{g.member_count} member{g.member_count === 1 ? "" : "s"} · <span className="font-mono text-gold-300">{g.code}</span></span>
                  {g.last_message && <span className="mt-1 flex items-center gap-1.5 text-xs text-slate-600 truncate"><MessageSquare className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{g.last_message.name}: {g.last_message.text || "sent a file"}</span></span>}
                </span>
                <ChevronRight className="w-5 h-5 text-slate-500" />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
